import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { encodeJson } from 'serialize-query-params';
import invariant from 'tiny-invariant';
import { useQueryParams } from 'use-query-params';

import type {
  EventLogField,
  SyntheticEventLogField,
} from '@packages/query-broker/deprecated-fields/event-log-field';
import {
  filterableFields,
  mapLegacySyntheticFields,
} from '@packages/query-broker/deprecated-fields/filterable-fields';
import type { ScalarBuilder } from '@packages/query-broker/operation-builder';
import { operation, field } from '@packages/query-broker/operation-builder';
import { isStringField } from '@packages/query-broker/utils/field-type-check/field-type-check';
import { ScalarBuilderQueryParam } from '@packages/query-broker/utils/scalar-builder-query-param/scalar-builder-query-param';

const legacyKeySet = new Set(Object.keys(filterableFields));

const fieldQueryParamConfigs = Object.keys(filterableFields).reduce(
  (acc, key) => {
    acc[key as EventLogField | SyntheticEventLogField] =
      ScalarBuilderQueryParam;
    return acc;
  },
  {} as Record<
    EventLogField | SyntheticEventLogField,
    typeof ScalarBuilderQueryParam
  >,
);

// TODO This will not work with synthetic events.
// Waiting on query broker to support them.
export const HandleLegacyFiltersGuard = () => {
  const location = useLocation();
  const [fields] = useQueryParams(fieldQueryParamConfigs);
  const scalars = Object.entries(fields)
    .filter(([_, value]) => value != null)
    .filter(([key]) => legacyKeySet.has(key))
    .map<[string, ScalarBuilder[] | undefined]>(([key, value]) => {
      key = mapLegacySyntheticFields.get(key) ?? key;
      key = key.replaceAll('__', '.');
      return [key, value];
    })
    .map<[string, ScalarBuilder[] | undefined]>(([key, value]) => [
      key.replaceAll('__', '.'),
      value,
    ]);

  if (scalars.length === 0) {
    return <Outlet />;
  }

  const currentSearchParams = new URLSearchParams(window.location.search);
  const newUrlSearchParams = new URLSearchParams();
  currentSearchParams.forEach((value, key) => {
    if (!legacyKeySet.has(key)) {
      if (key === 'columns') {
        // Handle multiple values for 'columns'
        newUrlSearchParams.append(key, value.replaceAll('__', '.'));
      } else {
        newUrlSearchParams.set(key, value);
      }
    }
  });

  const json = encodeJson(
    operation()
      .and(
        ...scalars.map(([key, value]) => {
          invariant(value);
          if (!isStringField(key)) {
            return operation().eq(field(key), value[0]);
          }
          return operation().in(field(key), value);
        }),
      )
      .build(),
  );

  invariant(json, 'expected filters');

  newUrlSearchParams.set('filters', json);

  return (
    <Navigate
      replace
      to={{
        pathname: location.pathname,
        search: newUrlSearchParams.toString(),
      }}
      state={location.state}
    />
  );
};
